import React, { FC, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Stack, Typography, useTheme } from '@mui/material';

import ButtonGroupToggle from '@/components/fields/ButtonGroupToggle';
import ChipSelect from '@/components/fields/ChipSelect';
import Field from '@/components/fields/Field';
import Slider from '@/components/fields/Slider';
import { getSecondsScales } from '@/containers/OrganizationSettings/dateOptions';
import { useIsMidotUser } from '@/hooks/permissions/useIsMidotUser';
import { useFieldPrefix } from '@/hooks/useFieldPrefix';
import typography from '@/theme/typography';

import { getWebcamRequiredOptions, WebcamMode } from './options';

const TestPropertiesTab: FC = () => {
  const { t } = useTranslation(['settings', 'common', 'tests']);
  const { spacing } = useTheme();

  const { watch, setValue } = useFormContext();
  const { getPrefixedFieldName } = useFieldPrefix();
  const webcam = watch(getPrefixedFieldName('webcam'));
  const proctoringAI = watch(getPrefixedFieldName('proctoringAI'));
  const isWebcamDisabled = webcam === WebcamMode.Disabled;
  const { isMidotUser } = useIsMidotUser();

  useEffect(() => {
    if (isWebcamDisabled && proctoringAI) {
      setValue(getPrefixedFieldName('proctoringAI'), false);
    }
  }, [isWebcamDisabled, proctoringAI, setValue, getPrefixedFieldName]);

  const secondsScales = useMemo(() => getSecondsScales(t), [t]);

  return (
    <Stack
      width="100%"
      sx={{
        '& .MuiFormLabel-root, & .MuiTypography-root': { textAlign: 'start' },
        '& .MuiFormControl-root': { p: 0 }
      }}
      py={3}
      gap={3}
    >
      <Typography variant="body1" sx={{ fontWeight: typography.fontWeightMedium }}>
        {t('processSettings.form.testProperties.clarificationTitle')}
      </Typography>
      <Field
        component={ButtonGroupToggle}
        name="enableClarificationQuestions"
        label={t('processSettings.form.testProperties.clarificationEnableQuestions')}
        withFormControl
        outerSx={{ alignItems: 'start' }}
      />

      <Divider />

      <Typography variant="body1" sx={{ fontWeight: typography.fontWeightMedium }}>
        {t('processSettings.form.testProperties.webcamTitle')}
      </Typography>
      <Stack direction="row" gap={3}>
        <Field
          component={ChipSelect}
          name="webcam"
          label={t('processSettings.form.testProperties.webcamRequired')}
          withFormControl
          outerSx={{ alignItems: 'start' }}
          innerProps={{
            options: getWebcamRequiredOptions(t),
            color: 'primary',
            enforcedValue: true
          }}
        />
        <Field
          component={ButtonGroupToggle}
          name="proctoringAI"
          label={t('processSettings.form.testProperties.webcamProctoringAI')}
          withFormControl
          outerSx={{ alignItems: 'start' }}
          disabled={isWebcamDisabled}
        />
      </Stack>

      {!isWebcamDisabled && isMidotUser && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gridColumnGap: spacing(3),
            gridRowGap: spacing(3)
          }}
        >
          <Field
            component={Slider}
            name="captureFrequency"
            label={t('processSettings.form.testProperties.webcamCaptureFrequency')}
            withFormControl
            disabled
            innerProps={{
              min: 1,
              max: 600,
              scales: secondsScales
            }}
          />
          <Box />
        </Box>
      )}

      {/* 
      <Divider />

      <Stack sx={{ '& .MuiFormControl-root': { pb: 0.5 } }}>
        <Typography variant="body1" sx={{ fontWeight: typography.fontWeightMedium }}>
          {t('processSettings.form.testProperties.normsTitle')}
        </Typography>
        {hasCustomizations && (
          <Stack>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '230px 230px 40px',
                gridColumnGap: spacing(1),
                gridRowGap: spacing(1),
                py: 2,
                '& .MuiToggleButtonGroup-root': {
                  alignSelf: 'start'
                },
                '& .CustomizationSection-columnTitle': {
                  color: 'grey.700',
                  textAlign: 'start',
                  ...typography.body3,
                  fontWeight: typography.fontWeightMedium
                },
                '& .DropdownSelect-menuList': {
                  maxHeight: 160
                }
              }}
            >
              <Typography className="CustomizationSection-columnTitle">
                {t('processSettings.form.testProperties.normsTestTypeName')}
              </Typography>
              <Typography className="CustomizationSection-columnTitle">
                {t('processSettings.form.testProperties.normsChoosePrimaryType')}
              </Typography>
              <Box />
              {customizations.map((_field, index: number) => (
                <>
                  <Field
                    component={DropdownSelect}
                    name={`customizations.${index}.test`}
                    innerProps={{
                      options: getTestsList(t),
                      isMulti: false,
                      usePortal: true
                    }}
                  />
                  <Field
                    component={DropdownSelect}
                    name={`customizations.${index}.normTypes`}
                    innerProps={{
                      options: getNormTypes(t),
                      usePortal: true,
                      isMulti: false
                    }}
                  />
                  <IconButton onClick={() => onRemoveCustomization(index)}>
                    <DeleteIcon />
                  </IconButton>
                </>
              ))}
            </Box>
          </Stack>
        )}
        <Button
          startIcon={<PlusCircleIcon />}
          sx={{
            textAlign: 'start',
            justifyContent: 'start',
            alignSelf: 'start',
            ...typography.body3,
            '& .MuiButton-icon': { mr: 0.5 }
          }}
          onClick={onAddCustomization}
        >
          {t('processSettings.form.testProperties.normsAddCustomization')}
        </Button>
      </Stack>
      */}
    </Stack>
  );
};

export default TestPropertiesTab;
